import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
// React 19 native metadata
import { ChevronLeft, ChevronRight, Link2, Check, BookOpen, Clock, ArrowUp } from 'lucide-react';
import ShareButton from '../common/ShareButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return 'Recently';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return dateStr; }
};

/* ── Reading Progress Bar ── */
const ReadingProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      const el = document.getElementById('blog-article-content');
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.scrollHeight;
      const scrolled = Math.max(0, -rect.top);
      setProgress(Math.min(100, (scrolled / (total - window.innerHeight)) * 100));
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-transparent">
      <div className="h-full bg-gradient-to-r from-purple-500 to-cyan-500 transition-all duration-100" style={{ width: `${progress}%` }} />
    </div>
  );
};

/* ── Table of Contents ── */
const TableOfContents = ({ html }) => {
  const headings = useMemo(() => {
    if (!html) return [];
    const matches = [...html.matchAll(/<h2[^>]*>(.*?)<\/h2>/gi)];
    return matches.map((m, i) => ({
      id: `toc-heading-${i}`,
      text: m[1].replace(/<[^>]+>/g, ''),
    }));
  }, [html]);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (headings.length < 3) return null;

  return (
    <nav className="my-10 p-6 rounded-xl bg-white/[0.03] border border-white/10" data-testid="blog-toc">
      <p className="text-xs font-semibold tracking-widest text-purple-400 uppercase mb-4 flex items-center gap-2">
        <BookOpen className="w-4 h-4" /> Table of Contents
      </p>
      <ol className="space-y-2">
        {headings.map((h, i) => (
          <li key={i}>
            <button
              onClick={() => scrollTo(h.id)}
              className="text-sm text-gray-400 hover:text-purple-300 transition-colors text-left leading-relaxed"
              data-testid={`toc-item-${i}`}
            >
              {i + 1}. {h.text}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
};

/* ── Inject IDs into H2 tags for TOC linking ── */
const injectHeadingIds = (html) => {
  if (!html) return html;
  let idx = 0;
  return html.replace(/<h2([^>]*)>/gi, (match, attrs) => {
    const id = `toc-heading-${idx++}`;
    if (attrs.includes('id=')) return match;
    return `<h2${attrs} id="${id}">`;
  });
};

/* ── Cross-link map: glossary terms & competitor comparisons (static) ── */
const STATIC_CROSS_LINKS = [
  { pattern: /\bAI marketing strategy\b/i, url: '/glossary/ai-marketing-strategy', label: 'AI marketing strategy' },
  { pattern: /\bmulti-agent AI\b/i, url: '/glossary/multi-agent-ai', label: 'multi-agent AI' },
  { pattern: /\bcompetitive intelligence\b/i, url: '/glossary/competitive-intelligence', label: 'competitive intelligence' },
  { pattern: /\bcontent intelligence\b/i, url: '/glossary/content-intelligence', label: 'content intelligence' },
  { pattern: /\bagentic AI\b/i, url: '/glossary/agentic-ai', label: 'agentic AI' },
  { pattern: /\bLiving War Room\b/i, url: '/glossary/living-war-room', label: 'Living War Room' },
  { pattern: /\bbrand voice AI\b/i, url: '/glossary/brand-voice-ai', label: 'brand voice AI' },
  { pattern: /\bstrategy score\b/i, url: '/glossary/strategy-score', label: 'strategy score' },
  { pattern: /\bcontent multiplication\b/i, url: '/glossary/content-multiplication', label: 'content multiplication' },
  { pattern: /\bSEO optimization\b/i, url: '/glossary/seo-optimization', label: 'SEO optimization' },
  { pattern: /\bviral growth\b/i, url: '/glossary/viral-growth', label: 'viral growth' },
  { pattern: /\bmarketing ROI\b/i, url: '/glossary/marketing-roi', label: 'marketing ROI' },
  { pattern: /\bPersonal God\b/, url: '/glossary/personal-god', label: 'Personal God' },
  { pattern: /\biSupplyAI vs Jasper\b/i, url: '/compare/jasper', label: 'iSupplyAI vs Jasper' },
  { pattern: /\biSupplyAI vs Copy\.ai\b/i, url: '/compare/copy-ai', label: 'iSupplyAI vs Copy.ai' },
  { pattern: /\biSupplyAI vs HubSpot\b/i, url: '/compare/hubspot-ai', label: 'iSupplyAI vs HubSpot' },
  { pattern: /\biSupplyAI vs MarketMuse\b/i, url: '/compare/marketmuse', label: 'iSupplyAI vs MarketMuse' },
  { pattern: /\biSupplyAI vs Surfer\b/i, url: '/compare/surfer-seo', label: 'iSupplyAI vs Surfer' },
  { pattern: /\biSupplyAI vs Writesonic\b/i, url: '/compare/writesonic', label: 'iSupplyAI vs Writesonic' },
  { pattern: /\biSupplyAI vs Semrush\b/i, url: '/compare/semrush', label: 'iSupplyAI vs Semrush' },
  { pattern: /\biSupplyAI vs ChatGPT\b/i, url: '/compare/chatgpt', label: 'iSupplyAI vs ChatGPT' },
  { pattern: /\biSupplyAI vs Perplexity\b/i, url: '/compare/perplexity', label: 'iSupplyAI vs Perplexity' },
  { pattern: /\biSupplyAI vs Ahrefs\b/i, url: '/compare/ahrefs', label: 'iSupplyAI vs Ahrefs' },
];

/* ── Smart cross-linking: inject links from static glossary + dynamic blog posts ── */
const injectCrossLinks = (html, currentSlug, blogLinks) => {
  if (!html) return html;
  const linked = new Set();
  let result = html;
  const MAX_LINKS = 8; // Cap total injected links
  let count = 0;

  // 1. Static glossary/comparison links first
  for (const { pattern, url, label } of STATIC_CROSS_LINKS) {
    if (count >= MAX_LINKS) break;
    if (linked.has(url)) continue;
    result = result.replace(new RegExp(`(?!<a[^>]*>)${pattern.source}(?![^<]*<\\/a>)`, pattern.flags), (match) => {
      if (linked.has(url) || count >= MAX_LINKS) return match;
      linked.add(url);
      count++;
      return `<a href="${url}" class="text-purple-400 hover:text-purple-300" data-crosslink="glossary">${match}</a>`;
    });
  }

  // 2. Dynamic blog-to-blog links from crosslinks API
  if (blogLinks && blogLinks.length > 0) {
    for (const link of blogLinks) {
      if (count >= MAX_LINKS) break;
      if (link.slug === currentSlug) continue;
      const blogUrl = `/blog/${link.slug}`;
      if (linked.has(blogUrl)) continue;

      // Try matching each tag keyword from the target post in this post's content
      for (const tag of (link.tags || [])) {
        if (count >= MAX_LINKS) break;
        if (tag.length < 5) continue;
        // Escape special regex chars in tag
        const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const tagPattern = new RegExp(`(?<!["\'>])(?<!</a)\\b${escaped}\\b(?![^<]*</a>)(?![^<]*">)`, 'i');
        const tagMatch = result.match(tagPattern);
        if (tagMatch) {
          const original = tagMatch[0];
          const linkHtml = `<a href="${blogUrl}" class="text-purple-400 hover:text-purple-300" data-crosslink="blog">${original}</a>`;
          result = result.replace(tagPattern, linkHtml);
          linked.add(blogUrl);
          count++;
          break; // One link per target post
        }
      }
    }
  }

  return result;
};

/* ── Extract FAQ pairs from HTML for schema ── */
const extractFAQs = (html) => {
  if (!html) return [];
  const faqs = [];
  // Match Q: ... A: ... patterns in <p> or <strong> tags
  const qPattern = /<p>\s*<strong>\s*Q:\s*(.*?)<\/strong>\s*<br\s*\/?>\s*(?:<strong>)?\s*A:\s*<\/strong>\s*(.*?)<\/p>/gi;
  let match;
  while ((match = qPattern.exec(html)) !== null) {
    const q = match[1].replace(/<[^>]+>/g, '').replace(/^Q:\s*/i, '').trim();
    const a = match[2].replace(/<[^>]+>/g, '').replace(/^A:\s*/i, '').trim();
    if (q && a) faqs.push({ question: q, answer: a });
  }
  // Also try pattern: <p><strong>Question?</strong><br/>Answer</p>
  if (faqs.length === 0) {
    const altPattern = /<p>\s*<strong>([^<]*\?)\s*<\/strong>\s*<br\s*\/?>\s*(.*?)<\/p>/gi;
    while ((match = altPattern.exec(html)) !== null) {
      const q = match[1].replace(/<[^>]+>/g, '').replace(/^Q:\s*/i, '').trim();
      const a = match[2].replace(/<[^>]+>/g, '').replace(/^A:\s*/i, '').trim();
      if (q && a && q.endsWith('?')) faqs.push({ question: q, answer: a });
    }
  }
  return faqs;
};

/* ── Inline Email Capture ── */
const InlineCapture = ({ slug, position }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/leads/blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source_post: slug, source: `blog_${position}` })
      });
      setSubmitted(true);
    } catch { setSubmitted(true); }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="my-10 p-8 rounded-2xl bg-gradient-to-br from-purple-900/30 to-cyan-900/20 border border-purple-500/20 text-center" data-testid={`blog-capture-${position}-success`}>
        <p className="text-xl font-bold text-white mb-2">You&apos;re in.</p>
        <p className="text-gray-400">We&apos;ll send you AI strategy insights that actually move the needle.</p>
      </div>
    );
  }

  return (
    <div className="my-10 p-8 rounded-2xl bg-gradient-to-br from-purple-900/30 to-cyan-900/20 border border-purple-500/20" data-testid={`blog-capture-${position}`}>
      <div className="max-w-lg mx-auto text-center">
        <p className="text-sm font-semibold tracking-widest text-purple-400 uppercase mb-2">Free Strategy Insights</p>
        <h3 className="text-xl font-bold text-white mb-2">Get AI marketing strategies that work</h3>
        <p className="text-gray-400 text-sm mb-5">Join founders and marketers getting weekly insights. No spam.</p>
        <form onSubmit={handleSubmit} className="flex gap-3 max-w-md mx-auto">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com" required
            className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-purple-500 transition-colors"
            data-testid={`blog-capture-${position}-email`}
          />
          <button type="submit" disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-xl text-sm hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-50 whitespace-nowrap"
            data-testid={`blog-capture-${position}-submit`}
          >
            {loading ? 'Joining...' : 'Subscribe'}
          </button>
        </form>
      </div>
    </div>
  );
};

/* ── Back to Top Button ── */
const BackToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const handler = () => setShow(window.scrollY > 800);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-purple-600/80 text-white flex items-center justify-center shadow-lg hover:bg-purple-500 transition-colors"
      data-testid="back-to-top-btn"
      aria-label="Back to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
};

/* ── Main Blog Post Component ── */
const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [adjacentPosts, setAdjacentPosts] = useState({ previous: null, next: null });
  const [blogLinks, setBlogLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Fetch post
    fetch(`${API_URL}/api/blog/posts/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(p => {
        setPost({
          slug: p.slug,
          title: p.title,
          excerpt: p.meta_description || '',
          content: p.content_markdown || '',
          contentHtml: p.content_html || '',
          heroImage: p.hero_image || 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800',
          category: p.category || 'AI Strategy',
          readTime: p.read_time || '5 min read',
          publishedAt: formatDate(p.published_at_display || p.published_at),
          rawDate: p.published_at_display || p.published_at || '',
          rawDateModified: p.updated_at || p.published_at_display || p.published_at || '',
          keywords: p.tags || [],
          author: p.author || 'iSupplyAI Editorial',
          wordCount: p.content_html ? p.content_html.replace(/<[^>]+>/g, '').split(/\s+/).length : 0,
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    // Fetch smart related posts (by category/tags)
    fetch(`${API_URL}/api/blog/related/${slug}?limit=4`)
      .then(r => r.json())
      .then(d => {
        setRelatedPosts((d.posts || []).map(p => ({
          slug: p.slug, title: p.title,
          heroImage: p.hero_image || 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800',
          category: p.category || 'AI Strategy',
          readTime: p.read_time || '5 min read',
        })));
      }).catch(() => {});

    // Fetch prev/next posts
    fetch(`${API_URL}/api/blog/adjacent/${slug}`)
      .then(r => r.json())
      .then(d => setAdjacentPosts(d))
      .catch(() => {});

    // Fetch crosslinks map for smart internal linking
    fetch(`${API_URL}/api/blog/crosslinks`)
      .then(r => r.json())
      .then(d => setBlogLinks(d.links || []))
      .catch(() => {});
  }, [slug]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(`https://isupplyai.com/blog/${slug}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [slug]);

  // Inject JSON-LD schemas into <head> manually (must be before early returns)
  useEffect(() => {
    if (!post) return;
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": (post.title || '').slice(0, 110),
      "description": post.excerpt || post.title,
      "image": {
        "@type": "ImageObject",
        "url": post.heroImage || "https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png",
        "width": 1200,
        "height": 675
      },
      "inLanguage": "en-US",
      "author": {
        "@type": "Organization",
        "name": "iSupplyAI",
        "url": "https://isupplyai.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png",
          "width": 512,
          "height": 512
        }
      },
      "publisher": {
        "@type": "Organization",
        "name": "iSupplyAI",
        "url": "https://isupplyai.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png",
          "width": 600,
          "height": 60,
          "caption": "iSupplyAI"
        }
      },
      "datePublished": post.rawDate || new Date().toISOString().split('T')[0],
      "dateModified": post.rawDateModified || post.rawDate || new Date().toISOString().split('T')[0],
      "mainEntityOfPage": { "@type": "WebPage", "@id": `https://isupplyai.com/blog/${post.slug}` },
      "keywords": (post.keywords || []).join(', '),
      "wordCount": post.wordCount || 0,
      "articleSection": post.category || "AI Strategy",
    };
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://isupplyai.com" },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://isupplyai.com/blog" },
        { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://isupplyai.com/blog/${post.slug}` }
      ]
    };
    const schemas = [articleSchema, breadcrumbSchema];
    // Extract and inject FAQ schema if post has FAQ section
    const faqs = extractFAQs(post.contentHtml);
    if (faqs.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": { "@type": "Answer", "text": f.answer },
        })),
      });
    }
    const scriptEls = schemas.map((schema, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `blogpost-schema-${i}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });
    return () => scriptEls.forEach(el => el.remove());
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (notFound || !post) return <Navigate to="/blog" replace />;

  const isHtmlContent = !!post.contentHtml && !post.content;
  const processedHtml = isHtmlContent ? DOMPurify.sanitize(injectCrossLinks(injectHeadingIds(post.contentHtml), slug, blogLinks)) : '';

  const shareUrl = encodeURIComponent(`https://isupplyai.com/blog/${post.slug}`);
  const shareTitle = encodeURIComponent(post.title);

  // Simple markdown-ish rendering (for non-HTML posts)
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="space-y-2 my-4 ml-4">
            {listItems.map((item, i) => (
              <li key={i} className="text-gray-300 leading-relaxed flex items-start gap-2">
                <span className="text-purple-400 mt-1.5 flex-shrink-0">&#8226;</span>
                <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>').replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-purple-400 hover:text-purple-300 underline">$1</a>') }} />
              </li>
            ))}
          </ul>
        );
        listItems = [];
      }
      inList = false;
    };

    const formatInline = (text) => DOMPurify.sanitize(text
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-purple-400 hover:text-purple-300 underline">$1</a>'));

    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (!trimmed) { if (inList) flushList(); continue; }
      if (trimmed.startsWith('### ')) { if (inList) flushList(); elements.push(<h3 key={`h3-${i}`} className="text-xl font-bold text-white mt-8 mb-4"><span dangerouslySetInnerHTML={{ __html: formatInline(trimmed.slice(4)) }} /></h3>); }
      else if (trimmed.startsWith('## ')) { if (inList) flushList(); elements.push(<h2 key={`h2-${i}`} className="text-2xl font-bold text-white mt-10 mb-4"><span dangerouslySetInnerHTML={{ __html: formatInline(trimmed.slice(3)) }} /></h2>); }
      else if (trimmed.startsWith('- ')) { inList = true; listItems.push(trimmed.slice(2)); }
      else { if (inList) flushList(); elements.push(<p key={`p-${i}`} className="text-gray-300 leading-relaxed my-4"><span dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }} /></p>); }
    }
    if (inList) flushList();
    const idx = Math.floor(elements.length * 0.4);
    elements.splice(idx, 0, <InlineCapture key="mid-capture" slug={slug} position="mid" />);
    return elements;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <ReadingProgress />
      <BackToTop />

      <>
        {/* SEO: smart-truncate to keep <title> under Google's 60-char SERP limit.
            If post.title alone is already long, drop the brand suffix; otherwise append a short brand. */}
        <title>{post.title.length > 47 ? post.title.slice(0, 60) : `${post.title} | iSupplyAI`}</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.keywords.join(', ')} />
        <link rel="canonical" href={`https://isupplyai.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.heroImage} />
        <meta property="og:url" content={`https://isupplyai.com/blog/${post.slug}`} />
        <meta property="article:published_time" content={post.rawDate} />
        <meta property="article:section" content={post.category} />
        {post.keywords.map((k, i) => <meta key={i} property="article:tag" content={k} />)}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.heroImage} />
      </>

      {/* Sticky Header */}
      <header className="border-b border-white/10 sticky top-0 bg-[#0a0a0f]/95 backdrop-blur-md z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-3" data-testid="blogpost-back-link">
            <ChevronLeft className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-400 hover:text-white transition-colors">All posts</span>
          </Link>
          <Link to="/" className="flex items-center gap-2" data-testid="blogpost-home-link">
            <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI Logo" title="iSupplyAI" className="h-8 w-8 object-contain" />
            <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">iSupplyAI</span>
          </Link>
        </div>
      </header>

      {/* Hero Image */}
      <div className="w-full h-64 md:h-96 relative">
        <img src={post.heroImage} alt={`${post.title} — ${post.category} | iSupplyAI`} title={post.title} className="w-full h-full object-cover" width="1200" height="630" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 -mt-16 relative z-10" id="blog-article-content">
        {/* Breadcrumbs */}
        <nav className="mb-6 text-xs text-gray-500 flex items-center gap-1.5" data-testid="blog-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-gray-300 transition-colors">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-400 truncate max-w-[200px]">{post.category}</span>
        </nav>

        {/* Post Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs font-semibold rounded-full">{post.category}</span>
            <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
            <span className="text-xs text-gray-500">{post.publishedAt}</span>
            {post.wordCount > 0 && <span className="text-xs text-gray-600">{post.wordCount.toLocaleString()} words</span>}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" data-testid="blogpost-title">
            {post.title}
          </h1>
          <p className="text-lg text-gray-400">{post.excerpt}</p>
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
            <span>By {post.author}</span>
          </div>
        </div>

        {/* Table of Contents */}
        {isHtmlContent && <TableOfContents html={post.contentHtml} />}

        {/* Content */}
        <div className="border-t border-white/10 pt-8" data-testid="blogpost-content">
          {isHtmlContent ? (
            <div className="prose prose-invert prose-purple max-w-none
              prose-h2:text-2xl prose-h2:font-bold prose-h2:text-white prose-h2:mt-10 prose-h2:mb-4 prose-h2:scroll-mt-20
              prose-h3:text-xl prose-h3:font-bold prose-h3:text-white prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-gray-300 prose-p:leading-relaxed prose-p:my-4
              prose-strong:text-white prose-strong:font-semibold
              prose-a:text-purple-400 prose-a:no-underline hover:prose-a:text-purple-300
              prose-li:text-gray-300 prose-li:leading-relaxed
              prose-ul:my-4 prose-ol:my-4
              prose-blockquote:border-purple-500/30 prose-blockquote:text-gray-400
            " dangerouslySetInnerHTML={{ __html: processedHtml }} />
          ) : (
            renderContent(post.content)
          )}
        </div>

        {/* Bottom Email Capture */}
        <InlineCapture slug={slug} position="bottom" />

        {/* Tags */}
        {post.keywords.length > 0 && (
          <div className="mt-8 pt-6 border-t border-white/10" data-testid="blog-tags">
            <p className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Topics</p>
            <div className="flex flex-wrap gap-2">
              {post.keywords.map((tag) => (
                <Link
                  key={tag}
                  to={`/blog?search=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-400 hover:text-purple-300 hover:border-purple-500/30 transition-colors"
                  data-testid={`blog-tag-${tag}`}
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Share */}
        <div className="border-t border-white/10 mt-8 pt-8">
          <p className="text-sm text-gray-500 mb-3">Share this article</p>
          <div className="flex flex-wrap gap-3">
            <a href={`https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/10 transition-colors" data-testid="blogpost-share-twitter">
              Share on X
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/10 transition-colors" data-testid="blogpost-share-linkedin">
              Share on LinkedIn
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/10 transition-colors" data-testid="blogpost-share-facebook">
              Share on Facebook
            </a>
            <button onClick={handleCopyLink}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/10 transition-colors flex items-center gap-2" data-testid="blogpost-copy-link">
              {copied ? <><Check className="w-4 h-4 text-green-400" /> Copied!</> : <><Link2 className="w-4 h-4" /> Copy link</>}
            </button>
          </div>
        </div>

        {/* Previous / Next Navigation */}
        {(adjacentPosts.previous || adjacentPosts.next) && (
          <nav className="border-t border-white/10 mt-8 pt-8 grid grid-cols-2 gap-4" data-testid="blog-adjacent-nav">
            {adjacentPosts.previous ? (
              <Link to={`/blog/${adjacentPosts.previous.slug}`} className="group text-left" data-testid="blog-prev-post">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Previous</p>
                <p className="text-sm text-gray-300 group-hover:text-purple-300 transition-colors line-clamp-2">{adjacentPosts.previous.title}</p>
              </Link>
            ) : <div />}
            {adjacentPosts.next ? (
              <Link to={`/blog/${adjacentPosts.next.slug}`} className="group text-right" data-testid="blog-next-post">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1 justify-end">Next <ChevronRight className="w-3 h-3" /></p>
                <p className="text-sm text-gray-300 group-hover:text-purple-300 transition-colors line-clamp-2">{adjacentPosts.next.title}</p>
              </Link>
            ) : <div />}
          </nav>
        )}
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-16 mt-8" data-testid="blog-related-section">
          <h2 className="text-2xl font-bold text-white mb-2">Related articles</h2>
          <p className="text-gray-500 text-sm mb-8">Continue reading about {post.category.toLowerCase()}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedPosts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group" data-testid={`blogpost-related-${p.slug}`}>
                <article className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden hover:border-purple-500/40 transition-all duration-300">
                  <img src={p.heroImage} alt={`${p.title} — ${p.category}`} title={p.title} className="w-full h-40 object-cover" loading="lazy" width="400" height="200" />
                  <div className="p-5">
                    <span className="text-xs text-purple-400 font-semibold">{p.category}</span>
                    <h3 className="text-sm font-bold text-white mt-2 group-hover:text-purple-300 transition-colors line-clamp-2">{p.title}</h3>
                    <p className="text-xs text-gray-500 mt-2">{p.readTime}</p>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to transform your marketing strategy?</h2>
          <p className="text-gray-400 mb-8">12 AI agents. Real debate. Battle-tested strategy. Free to start.</p>
          <Link to="/" className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all" data-testid="blogpost-cta-button">
            Try iSupplyAI Free
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} iSupplyAI, LLC. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
          </div>
        </div>
      </footer>
      <ShareButton title={post.title} description={post.excerpt} url={`https://isupplyai.com/blog/${post.slug}`} />
    </div>
  );
};

export default BlogPost;
